<template>
  <div
    class="ls-identify"
    @click.self="$emit('dismiss')"
  >
    <div
      ref="sheet"
      class="ls-identify__sheet"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="`${uid}-title`"
      :aria-describedby="`${uid}-why`"
      @keydown="onKeydown"
    >
      <h2
        :id="`${uid}-title`"
        class="ls-identify__title"
      >Who is this from?</h2>
      <p
        :id="`${uid}-why`"
        class="ls-identify__why"
      >So {{ brandName }} knows who liked what. No account needed.</p>
      <form
        class="ls-identify__form"
        novalidate
        @submit.prevent="submit"
      >
        <label
          class="ls-identify__label"
          :for="`${uid}-email`"
        >Email</label>
        <input
          :id="`${uid}-email`"
          ref="email"
          v-model="email"
          class="ls-identify__input"
          type="email"
          name="email"
          autocomplete="email"
          inputmode="email"
          autocapitalize="off"
          spellcheck="false"
          maxlength="254"
          required
          :aria-invalid="emailInvalid ? 'true' : 'false'"
          :aria-describedby="message ? `${uid}-error` : null"
        >
        <label
          class="ls-identify__label"
          :for="`${uid}-store`"
        >Store name <span class="ls-identify__optional">(optional)</span></label>
        <input
          :id="`${uid}-store`"
          v-model="storeName"
          class="ls-identify__input"
          type="text"
          name="organization"
          autocomplete="organization"
          maxlength="120"
          :aria-invalid="storeInvalid ? 'true' : 'false'"
        >
        <p
          v-if="message"
          :id="`${uid}-error`"
          class="ls-identify__error"
          data-test="ls-identify-error"
          role="alert"
        >{{ message }}</p>
        <div class="ls-identify__actions">
          <button
            type="button"
            class="ls-identify__later"
            data-test="ls-identify-later"
            @click="$emit('dismiss')"
          >Not now</button>
          <button
            type="submit"
            class="ls-identify__continue"
            data-test="ls-identify-continue"
            :disabled="busy"
          >{{ busy ? 'Saving' : 'Continue' }}</button>
        </div>
      </form>
      <p class="ls-identify__privacy">
        Shared with {{ brandName }} only.
        <a
          href="https://kingpin.global/privacy-policy.html"
          target="_blank"
          rel="noopener"
        >Privacy</a>
      </p>
    </div>
  </div>
</template>

<script>
import { isEmailLike } from './visitorIdentity'
import { trapTab } from './focusTrap'

const MESSAGES = Object.freeze({
  email: 'Enter an email like name@store.com.',
  store_name: 'Shorten the store name to 120 characters.',
  viewer_cap: 'This sheet cannot take more names right now. Your picks stay saved on this device.',
  rate_limited: 'Too many tries. Wait a minute and try again.',
  network: 'Could not save. Check your connection and try again. Your picks stay on this device.',
})

let seq = 0

/**
 * The light self-identification sheet (KP-4572), shown the first time a
 * visitor who did not come from an email link likes, marks interest or
 * comments. The action is already applied; this only attributes it. "Not now"
 * and Escape keep it on this device.
 */
export default {
  name: 'LsIdentifyDialog',
  props: {
    brandName: { type: String, required: true },
    initial: { type: Object, default: () => ({}) },
    busy: { type: Boolean, default: false },
    // { error, field } from the server, or null.
    error: { type: Object, default: null },
  },
  data() {
    seq += 1
    return {
      uid: `ls-identify-${seq}`,
      email: this.initial.email || '',
      storeName: this.initial.store_name || '',
      localError: '',
      returnFocus: null,
    }
  },
  computed: {
    serverKey() {
      if (!this.error || !this.error.error) return ''
      if (this.error.error === 'bad_request') return this.error.field === 'store_name' ? 'store_name' : 'email'
      return MESSAGES[this.error.error] ? this.error.error : 'network'
    },
    messageKey() {
      return this.localError || this.serverKey
    },
    message() {
      return this.messageKey ? MESSAGES[this.messageKey] : ''
    },
    emailInvalid() {
      return this.messageKey === 'email'
    },
    storeInvalid() {
      return this.messageKey === 'store_name'
    },
  },
  watch: {
    error() {
      this.localError = ''
    },
  },
  mounted() {
    this.returnFocus = document.activeElement
    this.$nextTick(() => this.$refs.email && this.$refs.email.focus())
  },
  beforeDestroy() {
    if (this.returnFocus && typeof this.returnFocus.focus === 'function') this.returnFocus.focus()
  },
  methods: {
    submit() {
      if (this.busy) return
      if (!isEmailLike(this.email)) {
        this.localError = 'email'
        this.$refs.email.focus()
        return
      }
      this.localError = ''
      this.$emit('submit', { email: this.email, store_name: this.storeName })
    },
    onKeydown(event) {
      if (event.key === 'Escape') {
        event.stopPropagation()
        this.$emit('dismiss')
        return
      }
      if (event.key === 'Tab') trapTab(this.$refs.sheet, event)
    },
  },
}
</script>

<style lang="scss" scoped>
@import './ls-base';

// A small sheet over the page, not a wall: the scrim stays light, the
// collection stays visible, and on wide screens it sits in the corner.
.ls-identify {
  position: fixed;
  inset: 0;
  z-index: 70;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  background: rgba(0, 0, 0, 0.24);
  animation: ls-identify-fade 0.2s $ls-ease both;

  @include ls-up($ls-md) {
    justify-content: flex-end;
    padding: 24px;
  }

  &__sheet {
    width: 100%;
    max-height: 92svh;
    overflow-y: auto;
    padding: 24px $ls-gutter calc(20px + env(safe-area-inset-bottom, 0px));
    background: var(--ls-ground);
    color: var(--ls-ink);
    border-radius: calc(var(--ls-radius) + 8px) calc(var(--ls-radius) + 8px) 0 0;
    box-shadow: 0 -12px 40px rgba(0, 0, 0, 0.18);
    animation: ls-identify-rise 0.28s $ls-ease both;

    @include ls-up($ls-md) {
      max-width: 400px;
      padding: 28px;
      border: 1px solid var(--ls-line);
      border-radius: calc(var(--ls-radius) + 4px);
      box-shadow: 0 24px 64px rgba(0, 0, 0, 0.2);
    }
  }

  &__title {
    @include ls-heading;
    margin: 0;
    font-size: 1.5rem;
  }

  &__why {
    margin: 8px 0 0;
    color: var(--ls-muted);
    font: 400 0.9375rem/1.5 var(--ls-body-font);
  }

  &__form {
    display: flex;
    flex-direction: column;
    margin-top: 20px;
  }

  &__label {
    margin: 12px 0 6px;
    @include ls-label;
    color: var(--ls-ink);
  }

  &__optional {
    color: var(--ls-muted);
    font-weight: 400;
    letter-spacing: 0;
    text-transform: none;
  }

  &__input {
    min-height: 48px;
    padding: 0 14px;
    border: 1px solid var(--ls-line);
    border-radius: var(--ls-radius);
    background: var(--ls-ground);
    color: var(--ls-ink);
    // 16px stops iOS zooming into the field.
    font: 400 1rem/1.2 var(--ls-body-font);

    &:focus {
      outline: 2px solid var(--ls-ink);
      outline-offset: 1px;
      border-color: var(--ls-ink);
    }

    &[aria-invalid='true'] {
      border-color: var(--ls-ink);
      border-width: 2px;
    }
  }

  &__error {
    margin: 12px 0 0;
    color: var(--ls-ink);
    font: 600 0.875rem/1.4 var(--ls-body-font);
  }

  &__actions {
    display: flex;
    gap: 10px;
    margin-top: 20px;
  }

  %ls-identify-button {
    flex: 1 1 0;
    min-height: 48px;
    border-radius: var(--ls-radius);
    font: 600 0.9375rem/1 var(--ls-body-font);
    cursor: pointer;
    touch-action: manipulation;
    @include ls-focus;
  }

  &__later {
    @extend %ls-identify-button;
    border: 1px solid var(--ls-line);
    background: transparent;
    color: var(--ls-ink);
  }

  &__continue {
    @extend %ls-identify-button;
    border: 1px solid var(--ls-primary);
    background: var(--ls-primary);
    color: var(--ls-on-primary);

    &:disabled {
      cursor: progress;
      opacity: 0.7;
    }
  }

  &__privacy {
    margin: 16px 0 0;
    color: var(--ls-muted);
    font: 400 0.8125rem/1.4 var(--ls-body-font);

    a {
      color: var(--ls-ink);
      text-underline-offset: 3px;
      @include ls-focus;
    }
  }
}

@keyframes ls-identify-fade {
  from {
    opacity: 0;
  }
}

@keyframes ls-identify-rise {
  from {
    transform: translateY(24px);
    opacity: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .ls-identify,
  .ls-identify__sheet {
    animation: none;
  }
}
</style>
